/**
 * "Headset Hero" mascot from design.md. Colors come from CSS variables
 * (--m-stroke / --m-body / --m-screen), so one SVG serves light and dark mode.
 * Earcups, gloves and sneakers keep their fixed colors per the usage rules.
 */
const S = "var(--m-stroke)";
const B = "var(--m-body)";
const SCREEN = "var(--m-screen)";

type Mood = "happy" | "thinking" | "celebrate";

function Face({ mood }: { mood: Mood }) {
  if (mood === "thinking") {
    return (
      <>
        <rect x="88" y="74" width="14" height="14" rx="7" fill="#FFB020" />
        <rect x="138" y="70" width="14" height="22" rx="7" fill="#FFB020" />
        <path d="M100 114 H140" fill="none" stroke="#FFB020" strokeWidth="5" strokeLinecap="round" />
      </>
    );
  }
  return (
    <>
      <rect x="88" y="68" width="14" height="24" rx="7" fill="#FFB020" />
      <rect x="138" y="68" width="14" height="24" rx="7" fill="#FFB020" />
      <path d={mood === "celebrate" ? "M90 104 Q120 136 150 104 Z" : "M94 108 Q120 128 146 108"} fill={mood === "celebrate" ? "#FFB020" : "none"} stroke="#FFB020" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
    </>
  );
}

export function Mascot({ mood = "happy", wave = false, className, label = "Departure Labs Support mascot" }: { mood?: Mood; wave?: boolean; className?: string; label?: string }) {
  const leftArm = mood === "celebrate" ? "M82 178 C64 168 52 150 50 128" : "M82 182 C60 176 44 160 40 142";
  const leftHand = mood === "celebrate" ? { cx: 50, cy: 120 } : { cx: 38, cy: 134 };
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="20 0 200 310" role="img" aria-label={label} className={className}>
      <path d="M106 232 V286 M134 232 V286" stroke={S} strokeWidth="7" strokeLinecap="round" />
      <path d="M88 292 C88 280 118 280 118 292 Z" fill="#1F4FD8" stroke={S} strokeWidth="4" strokeLinejoin="round" />
      <path d="M122 292 C122 280 152 280 152 292 Z" fill="#1F4FD8" stroke={S} strokeWidth="4" strokeLinejoin="round" />
      <path d="M44 74 C44 -2 196 -2 196 74" fill="none" stroke={S} strokeWidth="7" strokeLinecap="round" />
      <rect x="108" y="146" width="24" height="24" fill={B} stroke={S} strokeWidth="4" />
      <g className={wave ? "wave-arm" : undefined}>
        <path d={leftArm} fill="none" stroke={S} strokeWidth="7" strokeLinecap="round" />
        <circle {...leftHand} r="10" fill="#FFFFFF" stroke={S} strokeWidth="4" />
      </g>
      {mood === "celebrate" ? (
        <>
          <path d="M158 178 C176 168 188 150 190 128" fill="none" stroke={S} strokeWidth="7" strokeLinecap="round" />
          <circle cx="190" cy="120" r="10" fill="#FFFFFF" stroke={S} strokeWidth="4" />
        </>
      ) : mood === "thinking" ? (
        <>
          <path d="M158 184 C178 180 186 164 172 150" fill="none" stroke={S} strokeWidth="7" strokeLinecap="round" />
          <circle cx="168" cy="146" r="10" fill="#FFFFFF" stroke={S} strokeWidth="4" />
        </>
      ) : (
        <>
          <path d="M158 184 C178 186 190 196 184 212" fill="none" stroke={S} strokeWidth="7" strokeLinecap="round" />
          <circle cx="183" cy="216" r="10" fill="#FFFFFF" stroke={S} strokeWidth="4" />
        </>
      )}
      <rect x="82" y="166" width="76" height="66" rx="12" fill={B} stroke={S} strokeWidth="5" />
      <line x1="98" y1="182" x2="142" y2="182" stroke={S} strokeWidth="4" strokeLinecap="round" />
      <circle cx="120" cy="198" r="5" fill="#FF5A1F" stroke={S} strokeWidth="3" />
      <rect x="50" y="30" width="140" height="120" rx="18" fill={B} stroke={S} strokeWidth="6" />
      <rect x="64" y="44" width="112" height="92" rx="10" fill={SCREEN} />
      <path d="M72 54 H92" stroke="#FFFFFF" strokeOpacity="0.35" strokeWidth="4" strokeLinecap="round" />
      <Face mood={mood} />
      <rect x="32" y="64" width="20" height="46" rx="9" fill="#FF5A1F" stroke={S} strokeWidth="5" />
      <rect x="188" y="64" width="20" height="46" rx="9" fill="#FF5A1F" stroke={S} strokeWidth="5" />
      {mood !== "celebrate" && (
        <>
          <path d="M198 108 C200 140 186 148 166 148" fill="none" stroke={S} strokeWidth="5" strokeLinecap="round" />
          <circle cx="161" cy="148" r="7" fill={S} />
        </>
      )}
      {mood === "thinking" && <text x="196" y="40" fontFamily="IBM Plex Mono, monospace" fontWeight="600" fontSize="34" fill="#FFB020">?</text>}
    </svg>
  );
}

/** Head-only mark for the header (viewBox crop per design.md). */
export function MascotHead({ size = 34 }: { size?: number }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="28 0 184 160" width={size} height={size * (160 / 184)} aria-hidden="true">
      <path d="M44 74 C44 -2 196 -2 196 74" fill="none" stroke="#F4F1EA" strokeWidth="8" strokeLinecap="round" />
      <rect x="50" y="30" width="140" height="120" rx="18" fill="#2A3650" stroke="#F4F1EA" strokeWidth="7" />
      <rect x="64" y="44" width="112" height="92" rx="10" fill="#0B1220" />
      <rect x="88" y="68" width="14" height="24" rx="7" fill="#FFB020" />
      <rect x="138" y="68" width="14" height="24" rx="7" fill="#FFB020" />
      <path d="M94 108 Q120 128 146 108" fill="none" stroke="#FFB020" strokeWidth="6" strokeLinecap="round" />
      <rect x="32" y="64" width="20" height="46" rx="9" fill="#FF5A1F" stroke="#F4F1EA" strokeWidth="6" />
      <rect x="188" y="64" width="20" height="46" rx="9" fill="#FF5A1F" stroke="#F4F1EA" strokeWidth="6" />
    </svg>
  );
}
